import { MODULE_DESCRIPTIONS, MODULE_LABELS, MODULE_SETUP_STATUS_LABELS, type ProductModule, type WorkspaceModuleState } from '@lia/contracts';
import { Button, PageSkeleton, PermissionDenied, type StatusTone } from '@lia/ui';

import { Link, Outlet } from 'react-router';
import { useWorkspace } from '../../app/session.js';
import { usePageTitle } from '../../app/title.js';
import { QueryState } from '../../components/query-state.js';
import { useApiMutation, useApiQuery } from '../../lib/hooks.js';
import { wk } from '../../lib/query.js';

/** Module states for the current workspace (shared query with the rail). */
export function useWorkspaceModules() {
  const { workspaceId } = useWorkspace();
  return useApiQuery(wk.modules(workspaceId), 'listWorkspaceModules', { params: { workspaceId } }, { staleTime: 60_000 });
}

/** Summary status shown on Inicio and module landings: paused / setup needed / unavailable / active. */
export function moduleStatus(m: WorkspaceModuleState): { tone: StatusTone; label: string } {
  if (!m.enabled) return { tone: 'neutral', label: 'Pausado' };
  if (m.setupStatus === 'ready') return { tone: 'success', label: 'Activo' };
  if (m.setupStatus === 'unavailable') return { tone: 'warning', label: MODULE_SETUP_STATUS_LABELS.unavailable };
  return { tone: 'warning', label: MODULE_SETUP_STATUS_LABELS.needs_setup };
}

export function useToggleModule() {
  const { workspaceId } = useWorkspace();
  return useApiMutation('updateWorkspaceModule', {
    invalidate: [wk.modules(workspaceId), wk.home(workspaceId), wk.onboarding(workspaceId)],
    success: (m) => (m.enabled ? `${MODULE_LABELS[m.module]} activado` : `${MODULE_LABELS[m.module]} pausado`),
  });
}

/**
 * Route guard for a module's pages. A disabled module explains itself: admins get the enable action, everyone else
 * a no-access message (never a 404). The API still enforces every request.
 */
export function ModuleGate({ module }: { module: ProductModule }) {
  const modules = useWorkspaceModules();
  return (
    <QueryState query={modules} loading={<PageSkeleton />}>
      {(data) => {
        const m = data.items.find((x) => x.module === module);
        if (!m || m.enabled) return <Outlet />;
        return <ModuleDisabled state={m} />;
      }}
    </QueryState>
  );
}

function ModuleDisabled({ state }: { state: WorkspaceModuleState }) {
  const { isAdmin, workspaceId } = useWorkspace();
  const toggle = useToggleModule();
  const label = MODULE_LABELS[state.module];
  usePageTitle(label);
  const home = (
    <Button asChild variant="secondary">
      <Link to={`/w/${workspaceId}/inicio`}>Ir a Inicio</Link>
    </Button>
  );
  if (!isAdmin) {
    return (
      <PermissionDenied
        title={`${label} no está activo en este espacio`}
        description="Este módulo no está disponible para tu espacio de trabajo. Si lo necesitas, pide a un administrador que lo active."
        action={home}
      />
    );
  }
  return (
    <section aria-labelledby="module-off-title" className="mx-auto grid max-w-2xl gap-4 rounded-card border border-border-subtle bg-surface-default p-6 shadow-card">
      <h1 id="module-off-title" className="text-title font-semibold text-text-primary">
        {label} está pausado
      </h1>
      <p className="text-body text-text-primary">{MODULE_DESCRIPTIONS[state.module]}</p>
      <p className="text-body text-text-secondary">
        Mientras esté pausado, solo los administradores lo ven en el menú y el resto del equipo no tiene acceso a sus pantallas.
        {state.reason ? ` ${state.reason}` : ''}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button loading={toggle.isPending} onClick={() => toggle.mutate({ params: { workspaceId, module: state.module }, body: { enabled: true } })}>
          Activar módulo
        </Button>
        {state.module !== 'copilot' ? (
          <Button asChild variant="secondary">
            <Link to={`/w/${workspaceId}/integraciones`}>Revisar conexiones</Link>
          </Button>
        ) : null}
        {home}
      </div>
    </section>
  );
}

/** Admin-only switch to pause or enable a module from its landing page. */
export function ModuleToggleButton({ state }: { state: WorkspaceModuleState }) {
  const { isAdmin, workspaceId } = useWorkspace();
  const toggle = useToggleModule();
  if (!isAdmin) return null;
  return (
    <Button
      variant="secondary"
      loading={toggle.isPending}
      onClick={() => toggle.mutate({ params: { workspaceId, module: state.module }, body: { enabled: !state.enabled } })}
    >
      {state.enabled ? 'Pausar módulo' : 'Activar módulo'}
    </Button>
  );
}

